import { useCallback, useState } from "react";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import {
  AppointmentRecurrenceOptions,
  BookingStepBaseTestIDMap,
} from "@/constants/Booking";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useAppSelector } from "@/store/hooks";
import { selectRecurrence, useBookingDispatch } from "@/store/reducers/booking";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import BookingOptionCard from "@/ui/components/schedule/booking/BookingOptionCard";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { FlatList } from "@/ui/layout";

const AppointmentRecurrenceStep = ({ step }: BaseBookingStepProps) => {
  const initialRecurrence = useAppSelector(selectRecurrence);
  const { setRecurrence } = useBookingDispatch();

  const { onBackwardNavigation, onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });

  const { trackEvent } = useBookingAnalytics();
  const [selectedRecurrence, setSelectedRecurrence] = useState<
    EventRecurrence | undefined
  >(initialRecurrence);

  const handleForwardNavigation = () => {
    if (!selectedRecurrence) {
      return;
    }

    setRecurrence(selectedRecurrence);

    onForwardNavigation();
  };

  const baseTestID = BookingStepBaseTestIDMap[step];

  const ListHeaderComponent = useCallback(
    () => <BookingStepTitle step={step} />,
    [step],
  );

  return (
    <>
      <FlatList
        data={AppointmentRecurrenceOptions}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const isSelected = selectedRecurrence === item.recurrence;

          const handleSelectRecurrence = () => {
            if (item.recurrence !== selectedRecurrence) {
              trackEvent({
                event: BookingAnalyticsEvent.RecurrenceSelection,
                source: step,
                value: item.name,
              });
            }
            setSelectedRecurrence(item.recurrence);
          };

          return (
            <BookingOptionCard
              id={item.id}
              isSelected={isSelected}
              onSelect={handleSelectRecurrence}
              title={item.name}
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        isForwardNavigationDisabled={!selectedRecurrence}
        onBackwardPress={onBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentRecurrenceStep;
